import React, { useState } from 'react';
import { ChevronLeft, Copy, Volume2, FileDown } from 'lucide-react';
import { Summary } from '@/features/summaries/types';
import { useToast } from '@/hooks/use-toast';
import { DetailedSummaryViewer } from './DetailedSummaryViewer';
import { QuickSummaryViewer } from './QuickSummaryViewer';
import { MarkdownSummaryViewer } from './MarkdownSummaryViewer';

interface SummaryBulletPoint {
  text: string;
  citations: number;
}

interface SummaryContent {
  title: string;
  bulletPoints: SummaryBulletPoint[];
}

interface SummaryViewerProps {
  summary: Summary;
  onBack: () => void;
}

// Detect if content is markdown format
const isMarkdownContent = (content: string | null): boolean => {
  if (!content) return false;
  
  // Check for common markdown patterns
  return (
    content.startsWith('#') ||
    content.includes('\n## ') ||
    content.includes('\n### ') ||
    content.includes('**') ||
    content.includes('- ') ||
    content.includes('* ')
  );
};

const SummaryViewer: React.FC<SummaryViewerProps> = ({ summary, onBack }) => {
  // Check for markdown content FIRST - all AI-generated summaries now use markdown
  if (isMarkdownContent(summary.content)) {
    return <MarkdownSummaryViewer summary={summary} onBack={onBack} />;
  }

  // Fallback to specific viewers for non-markdown content (legacy support)
  if (summary.type === 'Detailed Summary') {
    return <DetailedSummaryViewer summary={summary} onBack={onBack} />;
  }

  if (summary.type === 'Quick Summary') {
    return <QuickSummaryViewer summary={summary} onBack={onBack} />;
  }

  // Executive Summary layout (fallback for non-markdown structured content)
  return <ExecutiveSummaryViewer summary={summary} onBack={onBack} />;
};

// Executive Summary Viewer component
const ExecutiveSummaryViewer: React.FC<SummaryViewerProps> = ({ summary, onBack }) => {
  const [copiedText, setCopiedText] = useState('');
  const { toast } = useToast();

  // Parse summary content - try JSON first, fall back to plain text
  const parsedContent: SummaryContent = React.useMemo(() => {
    if (!summary.content) {
      return { title: summary.title, bulletPoints: [] };
    }

    try {
      const parsed = JSON.parse(summary.content);
      if (parsed.title && Array.isArray(parsed.bulletPoints)) {
        return parsed as SummaryContent;
      }
    } catch {
      // If not JSON, treat as plain text and split by lines
    }

    // Fallback: split plain text content into bullet points
    const lines = summary.content.split('\n').filter(line => line.trim());
    return {
      title: summary.title,
      bulletPoints: lines.map(line => ({
        text: line.replace(/^[-•]\s*/, ''),
        citations: 0
      }))
    };
  }, [summary]);

  const handleCopy = () => {
    const textContent = `${parsedContent.title}\n\n${parsedContent.bulletPoints.map(bp => `• ${bp.text.replace(/<\/?strong>/g, '')}`).join('\n')}`;
    navigator.clipboard.writeText(textContent);
    setCopiedText('Copied!');
    setTimeout(() => setCopiedText(''), 2000);
  };

  const handleAudio = () => {
    toast({
      title: "Coming soon",
      description: "Text-to-speech will be available soon.",
    });
  };

  const handleSaveNote = () => {
    toast({
      title: "Coming soon",
      description: "Save as note will be available soon.",
    });
  };

  const handleDownload = () => {
    const textContent = `${parsedContent.title}\n\n${parsedContent.bulletPoints.map(bp => `• ${bp.text.replace(/<\/?strong>/g, '')}`).join('\n')}`;
    const blob = new Blob([textContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${summary.title}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const renderTextWithBold = (text: string) => {
    const parts = text.split(/(<strong>.*?<\/strong>)/g);
    return parts.map((part, index) => {
      if (part.startsWith('<strong>') && part.endsWith('</strong>')) {
        const content = part.replace(/<\/?strong>/g, '');
        return <strong key={index} className="font-semibold">{content}</strong>;
      }
      return <span key={index}>{part}</span>;
    });
  };

  return (
    <div className="flex h-full flex-col bg-background">
      {/* Back Button - Sticky */}
      <div className="sticky top-0 z-10 shrink-0 bg-background px-4 pb-2 pt-4">
        <button
          onClick={onBack}
          className="flex h-fit w-fit items-center gap-2 rounded-lg bg-transparent p-0 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="text-sm">Back</span>
        </button>
      </div>

      {/* Content Area */}
      <div className="relative flex-1 overflow-y-auto">
        <div className="px-4">
          <div className="prose prose-neutral dark:prose-invert max-w-none">
            <div className="space-y-4 inline">

              {/* Bullet Points List */}
              {parsedContent.bulletPoints.length > 0 ? (
                <ul className="list-outside list-disc pl-5 space-y-3">
                  {parsedContent.bulletPoints.map((point, index) => (
                    <li key={index} className="leading-7">
                      <p className="text-base leading-7 last:mb-0 text-foreground/90">
                        {renderTextWithBold(point.text)}
                        {' '}
                        {[...Array(point.citations)].map((_, i) => (
                          <span key={i} className="inline">
                            <sup className="cursor-pointer rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] text-primary transition-colors hover:bg-primary/20 ml-0.5">
                              s
                            </sup>
                          </span>
                        ))}
                      </p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted-foreground">No content available.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Action Bar - Bottom */}
      <div className="py-3 px-4 bg-background">
        <div className="flex flex-row items-center justify-between space-x-1">
          <div className="flex flex-row items-center space-x-1">
            {/* Copy Button */}
            <button
              onClick={handleCopy}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-colors hover:bg-muted hover:text-foreground h-7 w-7 p-1.5 text-muted-foreground"
              title="Copy to clipboard"
            >
              <Copy className="h-4 w-4" />
            </button>

            {/* Audio Button */}
            <button
              onClick={handleAudio}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-colors hover:bg-muted hover:text-foreground h-7 w-7 p-1.5 text-muted-foreground"
              title="Read aloud"
            >
              <Volume2 className="h-4 w-4" />
            </button>

            {/* Save as Note Button */}
            <button
              onClick={handleSaveNote}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-colors hover:bg-muted hover:text-foreground h-7 w-7 p-1.5 text-muted-foreground"
              title="Save as note"
            >
              <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 256 256" className="h-4 w-4" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
                <path d="M84,108A12,12,0,0,1,96,96h64a12,12,0,0,1,0,24H96A12,12,0,0,1,84,108Zm32,28H96a12,12,0,0,0,0,24h20a12,12,0,0,0,0-24ZM228,48V156.69a19.86,19.86,0,0,1-5.86,14.14l-51.31,51.31A19.86,19.86,0,0,1,156.69,228H48a20,20,0,0,1-20-20V48A20,20,0,0,1,48,28H208A20,20,0,0,1,228,48ZM52,204h92V156a12,12,0,0,1,12-12h48V52H52Zm139-36H168v23Z"></path>
              </svg>
            </button>

            {/* Download Button */}
            <button
              onClick={handleDownload}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-colors hover:bg-muted hover:text-foreground h-7 w-7 p-1.5 text-muted-foreground"
              title="Download"
            >
              <FileDown className="h-4 w-4" />
            </button>
          </div>

          {/* Copy confirmation text */}
          {copiedText && (
            <span className="text-xs text-correct animate-in fade-in-0">
              {copiedText}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default SummaryViewer;
